import styled from "@emotion/styled";
import * as React from "react";
import { useSelector } from "react-redux";

import { SpacingToken } from "@livechat/design-system-react-components";

import { ResponsiveBreakpoint } from "constants/responsive-breakpoint";
import { getSubscriptionPlans } from "helpers/subscription-plans";
import { IBilling, selectHasCreditCard } from "store/entities/billing";
import { ISubscription, Plan, selectCurrentSubscriptionPlan } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";

import { PlanTile } from "./PlanTile";
import { getIsGrandfathering, getPrice } from "./helpers";

interface IProps {
  subscription: ISubscription;
  newSubscription: ISubscription;
  recommendedPlan: Plan | null;
  billing: IBilling | null;
  nonViewersCount: number;
  onPlanChange(plan: Plan): void;
}

const getPlansToDisplay = (currentPlan: Plan): Plan[] => {
  switch (currentPlan) {
    case Plan.Team:
    case Plan.Business:
      return [Plan.Team, Plan.Business, Plan.Enterprise];
    default:
      return [Plan.Team, Plan.Business, Plan.Enterprise];
  }
};

export const PlansTiles: React.FC<IProps> = ({ subscription, newSubscription, onPlanChange, recommendedPlan }) => {
  const hasCreditCard = useSelector(selectHasCreditCard);
  const currentPlan = useSelector(selectCurrentSubscriptionPlan);

  const { plan: selectedPlan } = newSubscription;
  const plansToDisplay = getPlansToDisplay(currentPlan);
  const plans = getSubscriptionPlans(plansToDisplay);

  React.useEffect(() => {
    if (window.location.hash === "#upgrade-team") {
      onPlanChange(Plan.Team);
    }

    if (window.location.hash === "#upgrade-enterprise") {
      onPlanChange(Plan.Enterprise);
    }

    if (window.location.hash === "#upgrade-business") {
      onPlanChange(Plan.Business);
    }
  }, [onPlanChange]);

  return (
    <PlansWrapper>
      {plans.map(planElement => {
        const isGrandfathering = getIsGrandfathering(planElement.plan, subscription, newSubscription);

        const shouldDisablePlan = currentPlan === Plan.Enterprise && planElement.plan !== Plan.Enterprise;

        const shouldRecommendPlan =
          planElement.plan === recommendedPlan && recommendedPlan !== Plan.Enterprise && !hasCreditCard;

        return (
          <PlanTile
            key={planElement.plan}
            planType={planElement.plan}
            planName={planElement.name}
            active={planElement.plan === currentPlan}
            disabled={shouldDisablePlan}
            recommended={shouldRecommendPlan}
            selected={planElement.plan === selectedPlan}
            oldPrice={isGrandfathering ? planElement.price[subscription.billingCycle] : undefined}
            price={getPrice(planElement, isGrandfathering, subscription, newSubscription)}
            onClick={() => onPlanChange(planElement.plan)}
          />
        );
      })}
    </PlansWrapper>
  );
};

const PlansWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${withVar(SpacingToken.Spacing2)};
  @media (max-width: ${ResponsiveBreakpoint.Tablet}px) {
    flex-direction: column;
  }
`;
